import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { trackRegistrationClick } from '../api/api';
import { isStandalone, isIOS, registerSW } from '../utils/push';
import ActivityJoinSection from '../components/ActivityJoinSection';
import ActivityRegistrationBlock from '../components/ActivityRegistrationBlock';
import RegistrationOTPModal from '../components/RegistrationOTPModal';
import { InterestIcon } from '../components/admin/AdminInterests';
import { useAuth } from '../context/AuthContext';
import { VerifiedBadge, UnverifiedBadge } from '../components/VerifiedBadge';
import { useChangemakerAuth } from '../context/ChangemakerAuthContext';
import ActivityEditPage from './ActivityEditPage';
import HeartButton from '../components/HeartButton';
import ActivityLocationMap from '../components/ActivityLocationMap';
import ActivityJourney from '../components/ActivityJourney';
import ActivityCommentsSection from '../components/ActivityCommentsSection';
import { Home, QrCode, Share2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import ExploreAfterExpiredSection from '../components/ExploreAfterExpiredSection';
import ShareButton from '../components/ShareButton';
import {
  getActivity,
  getChangemaker,
  getInterests,
  getEngagement,
  toggleActivityInterest,
  updateActivity,
  publishActivity,
  adminUploadMedia,
  getActivityUpdates,
} from '../api/api';
import { formatDateRange } from '../utils/dates';
import { track, setCurrentPage } from '../utils/analytics';
import { ArrowLeft, MapPin, Clock, X, ChevronLeft, ChevronRight, ArrowRight, ExternalLink, FileText, Download, Globe, Lock } from 'lucide-react';
import { getFormEmbedUrl } from '../utils/formEmbed';
import SEOHead, { truncateDesc, toAbsoluteUrl } from '../components/SEOHead';
import { SafeHtml } from '../components/SafeHtml';

// ── SEO helpers ───────────────────────────────────────────────────────────────

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Slots left badge ─────────────────────────────────────────────────────────
function SlotsLeftBadge({ slotsLeft, className = '' }) {
  if (slotsLeft === null || slotsLeft === undefined) return null;
  const colorCls = slotsLeft >= 10
    ? 'text-green-700 bg-green-50 border-green-100'
    : slotsLeft >= 5
    ? 'text-yellow-700 bg-yellow-50 border-yellow-100'
    : 'text-amber-700 bg-amber-50 border-amber-100';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border inline-block ${colorCls} ${className}`}
       data-testid="slots-left-badge">
      {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} left
    </span>
  );
}

function formatSocialDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${d} ${MONTH_SHORT[m - 1]} '${String(y).slice(2)}`;
}

function buildActivitySocialDesc(activity) {
  const parts = [];
  const shortDesc = truncateDesc(activity.description || '', 90);
  if (shortDesc) parts.push(shortDesc);
  if (activity.date) parts.push(formatSocialDate(activity.date));
  const addr = activity.location?.address;
  if (addr) parts.push(addr);
  return parts.join('\n');
}

function getActivityImage(activity) {
  return toAbsoluteUrl(activity.image_url) ||
    toAbsoluteUrl((activity.media_urls || [])[0]) ||
    null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const isVideoUrl = (url) => /\.(mp4|mov|webm)(\?.*)?$/i.test(url || '');

// ── Activity Update helpers ───────────────────────────────────────────────────

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatUpdateTimestamp(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr || '';
    const day = d.getDate();
    const mon = MONTHS[d.getMonth()];
    const yr = d.getFullYear();
    let h = d.getHours(), m = d.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    const mm = String(m).padStart(2, '0');
    return `${day} ${mon} ${yr}, ${h}:${mm} ${ampm}`;
  } catch { return isoStr || ''; }
}

function UpdateMediaCarousel({ urls, mediaNames }) {
  const [idx, setIdx] = useState(0);
  if (!urls?.length) return null;
  const isVid = (u) => /\.(mp4|mov|webm|avi|wmv|mpg|mpeg)(\?.*)?$/i.test(u || '');
  const isPdf = (u) => /\.pdf(\?.*)?$/i.test(u || '');
  const current = urls[idx];
  const name = mediaNames?.[idx] ? mediaNames[idx] : '';

  return (
    <div className="rounded-xl overflow-hidden bg-gray-50 mt-2 mb-2">
      {/* Media */}
      {isPdf(current) ? (
        <div className="flex flex-col items-center justify-center gap-3 py-8 px-4 bg-red-50 min-h-[140px]">
          <FileText size={36} className="text-red-400" />
          <p className="text-sm font-semibold text-gray-700 text-center">{name || 'PDF Document'}</p>
          <a
            href={current}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors"
          >
            <Download size={13} /> Open / Download PDF
          </a>
        </div>
      ) : isVid(current) ? (
        <div style={{ aspectRatio: '4/3' }}>
          <video src={current} controls className="w-full h-full object-contain" />
        </div>
      ) : (
        <div style={{ aspectRatio: '4/3' }}>
          <img src={current} alt={name || 'update media'} className="w-full h-full object-contain" />
        </div>
      )}
      {/* Prev/Next + dots */}
      {urls.length > 1 && (
        <div className="relative">
          <button onClick={() => setIdx((idx - 1 + urls.length) % urls.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1.5 hover:bg-black/60">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setIdx((idx + 1) % urls.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1.5 hover:bg-black/60">
            <ChevronRight size={16} />
          </button>
          <div className="flex justify-center gap-1 pb-2 pt-1">
            {urls.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-gray-800' : 'bg-gray-300'}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lightbox ─────────────────────────────────────────────────────────────────

function Lightbox({ photos, idx, onClose, onSetIdx }) {
  const current = photos[idx];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onSetIdx((idx + 1) % photos.length);
      if (e.key === 'ArrowLeft') onSetIdx((idx - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, photos.length]);

  return (
    <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/40 hover:bg-black/60" data-testid="lightbox-close">
        <X size={20} />
      </button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onSetIdx((idx - 1 + photos.length) % photos.length); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/40 hover:bg-black/60">
            <ChevronLeft size={22} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onSetIdx((idx + 1) % photos.length); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/40 hover:bg-black/60">
            <ChevronRight size={22} />
          </button>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-3xl max-h-[90vh] w-full px-4">
        {isVideoUrl(current) ? (
          <video src={current} controls autoPlay className="max-h-[85vh] w-full object-contain rounded-lg" />
        ) : (
          <img src={current} alt="" className="max-h-[85vh] w-full object-contain rounded-lg" />
        )}
        {photos.length > 1 && (
          <p className="text-white/60 text-xs text-center mt-2">{idx + 1} / {photos.length}</p>
        )}
      </div>
    </div>
  );
}

// ── EditorQR component (used by the editor options panel) ────────────────────

function EditorQR({ url, slug }) {
  const [open, setOpen] = useState(false);

  const downloadPNG = () => {
    const canvas = document.getElementById('editor-qr-canvas')?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${slug || 'activity'}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const downloadSVG = () => {
    const svgEl = document.getElementById('editor-qr-svg')?.querySelector('svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${slug || 'activity'}-qr.svg`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="editor-generate-qr"
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-700 hover:bg-white transition-colors"
      >
        <QrCode size={13} />
        {open ? 'Hide QR Code' : 'Generate QR Code'}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-white rounded-xl border border-gray-200 flex flex-col items-center gap-2">
          <p className="text-[11px] text-gray-400 text-center">Scan to open this activity</p>
          <div id="editor-qr-canvas" className="bg-white p-1.5 rounded-md">
            <QRCodeCanvas value={url} size={150} bgColor="#ffffff" fgColor="#111827" />
          </div>
          <div id="editor-qr-svg" style={{ display: 'none' }}>
            <QRCodeSVG value={url} size={150} />
          </div>
          <div className="flex gap-2">
            <button onClick={downloadPNG} data-testid="editor-qr-download-png" className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700">Download PNG</button>
            <button onClick={downloadSVG} data-testid="editor-qr-download-svg" className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Download SVG</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function ActivityDetailPage() {
  const { id: slugOrId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, setUser, openAuthModal, loading: authLoading } = useAuth();
  const { cmOrg, loading: cmOrgLoading } = useChangemakerAuth();

  // Owner edit mode (for published activities).
  // Initialized synchronously from URL so there is no public-page flash when
  // the user arrives via the Edit button (?mode=edit).
  const [ownerEditMode, setOwnerEditMode] = useState(
    () => new URLSearchParams(window.location.search).get('mode') === 'edit'
  );

  // Sticky heading
  const headingSentinelRef = useRef(null);
  const [headingSticky, setHeadingSticky] = useState(false);

  // PWA install CTA
  const isInStandalone = useMemo(() => isStandalone(), []);
  const isOnIOS = useMemo(() => isIOS(), []);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);

  useEffect(() => { registerSW(); }, []);
  useEffect(() => {
    if (window.__karmana_pwa_prompt) setInstallPromptEvent(window.__karmana_pwa_prompt);
    const handler = (e) => {
      e.preventDefault();
      window.__karmana_pwa_prompt = e;
      setInstallPromptEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  useEffect(() => {
    const handler = () => { setInstallPromptEvent(null); };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const handleInstallClick = useCallback(() => {
    if (isOnIOS) return; // no iOS guide on detail page; let browser handle
    const prompt = installPromptEvent || window.__karmana_pwa_prompt;
    if (prompt) {
      prompt.prompt();
      prompt.userChoice.then(() => {
        setInstallPromptEvent(null);
        window.__karmana_pwa_prompt = null;
      }).catch(() => {});
    }
  }, [isOnIOS, installPromptEvent]);

  const installCTANode = !isInStandalone ? (
    <button
      onClick={handleInstallClick}
      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors whitespace-nowrap"
      data-testid="pwa-install-btn-detail"
      aria-label="Add Karmana Journeys to home screen"
    >
      <Home size={10} strokeWidth={2.5} />
      Add to Home Page
    </button>
  ) : null;

  // Bottom sentinel — when visible, sticky buttons unpin
  const bottomSentinelRef = useRef(null);
  const [bottomVisible, setBottomVisible] = useState(false);

  // Carousel
  const carouselRef = useRef(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [detailImgNaturals, setDetailImgNaturals] = useState({});
  const [detailCarouselHeight, setDetailCarouselHeight] = useState(null);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const lightboxOpenTimeRef = useRef(null);

  // Interest display count (separate from server-backed engagement query)
  const [interestCount, setInterestCount] = useState(0);

  // Compute carousel height from actual image dimensions (max height, capped at aspect-[4/5])
  useEffect(() => {
    const keys = Object.keys(detailImgNaturals);
    if (keys.length === 0) return;
    const containerW = carouselRef.current?.offsetWidth;
    if (!containerW) return;
    const maxDefaultH = Math.round(containerW * 5 / 4);
    let maxH = 0;
    keys.forEach(k => {
      const { w, h } = detailImgNaturals[k];
      if (w > 0) maxH = Math.max(maxH, Math.min(Math.round(h * containerW / w), maxDefaultH));
    });
    if (maxH > 0) setDetailCarouselHeight(maxH);
  }, [detailImgNaturals]);
  const [interestLoading, setInterestLoading] = useState(false);

  // Registration state
  // OTP activation modal (page-level so it persists after form unmounts)
  const [otpData, setOtpData] = useState(null); // { method, email, pendingRegId, confirmResult }
  // Tracks pending OTP for "You're all set. Sign up" CTA (GAP 3+4)
  const [pendingOTPForSignUp, setPendingOTPForSignUp] = useState(null);
  const regBlockRef = useRef(null);
  const autoScrollRegDone = useRef(false);
  const journeyRef = useRef(null);
  const autoScrollJourneyDone = useRef(false);
  // Karmana: message shown when user clicks "I'm Interested" to scroll+highlight the form
  const [showRegMessage, setShowRegMessage] = useState(false);
  // Journey: countdown after Karmana registration success (non-dismissible)
  const [regSuccessJourneyCountdown, setRegSuccessJourneyCountdown] = useState(null);

  // ── Signup attribution for explore-after-expired experiment ──────────────
  useEffect(() => {
    if (!user) return;
    try {
      const ref = sessionStorage.getItem('karmana_explore_ref');
      if (ref) {
        const parsed = JSON.parse(ref);
        track('explore_expired_signup', {
          activity_id:     parsed.from_slug,
          to_activity_id:  parsed.to_activity_id,
          criteria_level:  parsed.criteria_level,
        });
        sessionStorage.removeItem('karmana_explore_ref');
      }
    } catch {}
  }, [user]);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const { data: activity, isLoading, isError, error } = useQuery({
    queryKey: ['activity', slugOrId],
    queryFn: async () => (await getActivity(slugOrId)).data,
    staleTime: 60_000,
    retry: (failureCount, err) => {
      const s = err?.response?.status;
      return ![401, 403, 404].includes(s) && failureCount < 3;
    },
    // Seed from the activities-list cache so navigation feels instant
    placeholderData: () => {
      const list = queryClient.getQueryData(['activities']);
      if (Array.isArray(list)) {
        return list.find(a => a.slug === slugOrId || a.id === slugOrId);
      }
      return undefined;
    },
  });

  // Fetch organizer: use cached changemakers list if available, else fetch just the one
  const changemakerId = activity?.changemaker_id;
  const { data: organizer } = useQuery({
    queryKey: ['changemaker', changemakerId],
    queryFn: async () => {
      const cached = queryClient.getQueryData(['changemakers']);
      if (cached) {
        const found = cached.find((c) => c.id === changemakerId);
        if (found) return found;
        // Fall through to direct fetch — org may not be in the public changemakers list
      }
      const { data } = await getChangemaker(changemakerId);
      return data;
    },
    enabled: !!changemakerId,
    staleTime: 300_000,
  });

  const { data: interests = [] } = useQuery({
    queryKey: ['interests'],
    queryFn: async () => (await getInterests()).data,
    staleTime: 300_000,
  });

  // Per-activity engagement state — single server-backed source of truth for the CTA.
  // Replaces the old separate checkActivityInterest + getMyRegistration calls.
  const { data: engagement } = useQuery({
    queryKey: ['engagement', activity?.id],
    queryFn: async () => (await getEngagement(activity.id)).data,
    enabled: !!user && !!activity?.id,
  });
  // Derived convenience values — all downstream code uses these names unchanged
  const interested = engagement?.interested ?? false;
  const alreadyRegistered = engagement?.registration_completed ?? false;

  // Activity updates (public — shown on detail page)
  const { data: activityUpdates = [] } = useQuery({
    queryKey: ['activity-updates', activity?.id],
    queryFn: async () => (await getActivityUpdates(activity.id)).data,
    enabled: !!activity?.id,
    staleTime: 60_000,
  });

  // Handle ?mode=edit and ?mode=view — enables org management view for owners AND editor collaborators
  useEffect(() => {
    if (!activity?.id) return;
    const mode = searchParams.get('mode');
    if (mode !== 'edit' && mode !== 'view') return;
    const isOwnerCheck = cmOrg?.id === activity.changemaker_id;
    const isEditorCollab = (activity.collaborators || []).some(
      c => c.org_id === cmOrg?.id && c.permission === 'editor'
    );
    if (!isOwnerCheck && !isEditorCollab) {
      const authSettled = !authLoading && !cmOrgLoading;
      if (authSettled) setOwnerEditMode(false);
      return;
    }
    // Confirmed owner or editor collaborator — enable management view
    setOwnerEditMode(true);
  }, [activity?.id, activity?.collaborators, cmOrg?.id, cmOrgLoading, authLoading]); // eslint-disable-line

  // Initialise display count from activity data; engagement state is driven by the query above
  useEffect(() => {
    if (!activity?.id) return;
    setInterestCount(activity.interested_count ?? 0);
    window.prerenderReady = true;
  }, [activity?.id]); // eslint-disable-line

  // Clear pending OTP state when user becomes authenticated (e.g. via main auth flow)
  useEffect(() => {
    if (user) setPendingOTPForSignUp(null);
  }, [user?.id]); // eslint-disable-line

  // Handle ?register=1 — auto-scroll to registration section
  useEffect(() => {
    const shouldRegister = searchParams.get('register') === '1';
    if (!shouldRegister || !interested || !regBlockRef.current || autoScrollRegDone.current) return;
    autoScrollRegDone.current = true;
    // Clean URL
    const next = new URLSearchParams(searchParams);
    next.delete('register');
    setSearchParams(next, { replace: true });
    // Scroll to reg block
    setTimeout(() => {
      regBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  }, [searchParams, interested, activity?.id]); // eslint-disable-line

  // Handle ?section=journey — auto-scroll to Activity Journey section
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'journey' || !activity?.id || autoScrollJourneyDone.current) return;
    if (!journeyRef.current) return;
    autoScrollJourneyDone.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('section');
    setSearchParams(next, { replace: true });
    setTimeout(() => {
      journeyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);
  }, [searchParams, activity?.id, journeyRef.current]); // eslint-disable-line

  // ── Capture share ref on mount (non-blocking, cleans URL immediately) ─────
  const shareRefCapture = useRef(null);
  useEffect(() => {
    const r = searchParams.get('ref');
    if (r) {
      shareRefCapture.current = r;
      const next = new URLSearchParams(searchParams);
      next.delete('ref');
      setSearchParams(next, { replace: true });
    }
  }, []); // mount only

  // Track page view
  useEffect(() => {
    if (activity?.id) {
      setCurrentPage('activity_detail');
      const detailIsPast = activity.is_ongoing
        ? (!!activity.date_end && new Date(`${activity.date_end}T23:59:59`) < new Date())
        : (!!activity.date && new Date(`${activity.date}T23:59:59`) < new Date());
      track('page_view', {
        page: 'activity_detail',
        activity_id: activity.id,
        slug: activity.slug,
        activity_name: activity.name,
        activity_date: activity.date || null,
        activity_date_end: activity.date_end || null,
        activity_location_locality: activity.location?.locality || null,
        activity_interest_tags: interests.length > 0
          ? (activity.areas_of_interest || []).map(id => interests.find(i => i.id === id)?.name || id)
          : (activity.areas_of_interest || []),
        organisation_id: activity.changemaker_id || null,
        organisation_name: organizer?.name || null,
        registration_type: activity.registration_type || null,
        is_past: detailIsPast,
        ...(shareRefCapture.current ? { source: 'activity_share', ref: shareRefCapture.current } : {}),
      });
    }
  }, [activity?.id]);

  // ── Observers ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const obs = (ref, cb) => {
      const o = new IntersectionObserver(([e]) => cb(!e.isIntersecting), { threshold: 0 });
      if (ref.current) o.observe(ref.current);
      return o;
    };
    const h = obs(headingSentinelRef, setHeadingSticky);

    // Bottom sentinel: fix footer only when its natural position is BELOW the viewport.
    // When sentinel is visible OR has scrolled above the viewport, the footer is unpinned.
    const b = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Sentinel entered the viewport — footer's natural position is now in view
          setBottomVisible(true);
        } else if (entry.boundingClientRect.top < 0) {
          // Sentinel exited above the viewport — user scrolled past — keep unpinned
          setBottomVisible(true);
        } else {
          // Sentinel is below the viewport — footer not yet reached — pin to bottom
          setBottomVisible(false);
        }
      },
      { threshold: 0 }
    );
    if (bottomSentinelRef.current) b.observe(bottomSentinelRef.current);

    return () => { h.disconnect(); b.disconnect(); };
  }, [activity?.id]);

  // ── Carousel helpers ──────────────────────────────────────────────────────

  const carouselPhotos = (() => {
    if (!activity) return [];
    const items = [];
    if (activity.video_url) items.push(activity.video_url);
    if (activity.media_urls?.length) items.push(...activity.media_urls);
    return [...new Set(items)];
  })();

  const handleCarouselScroll = (e) => {
    const newIdx = Math.round(e.currentTarget.scrollLeft / e.currentTarget.offsetWidth);
    if (newIdx !== carouselIdx) {
      track('activity_media_scroll', { activity_id: activity?.id, media_index_after: newIdx, media_count: carouselPhotos.length });
    }
    setCarouselIdx(newIdx);
  };

  // ── Post-signup auto-interest: replay pending interest from ?autoInterest param ──
  const autoInterestDone = useRef(false);
  const [autoShowSuccess, setAutoShowSuccess] = useState(false);
  useEffect(() => {
    const autoInterestId = searchParams.get('autoInterest');
    if (!autoInterestId || !user || !activity || autoInterestDone.current) return;
    if (autoInterestId !== activity.id) return;
    autoInterestDone.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('autoInterest');
    setSearchParams(next, { replace: true });
    localStorage.removeItem('karmana_pending_interest');
    if (!interested) {
      toggleActivityInterest(activity.id).then(async r => {
        if (r.data) {
          queryClient.setQueryData(['engagement', activity.id], r.data);
          if (r.data.interested) setInterestCount(c => c + 1);
          await queryClient.refetchQueries({ queryKey: ['my-activities'] });
        }
        track('post_signup_action_completed', { page: 'activity_detail', activity_id: activity.id, organisation_id: activity.changemaker_id || null });
        // If activity has journey, scroll to it; otherwise show success modal
        if (activity.activity_journey === true) {
          setTimeout(() => journeyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 600);
        } else {
          setAutoShowSuccess(true);
        }
      }).catch(() => {});
    } else if (activity.activity_journey === true) {
      // Already interested and journey enabled — just scroll to journey
      setTimeout(() => journeyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [searchParams, user, activity, interested]); // eslint-disable-line

  // ── Interested handler ────────────────────────────────────────────────────

  const handleInterested = () => {
    const isKarmana = activity?.registration_type === 'karmana';
    const orgId = activity?.changemaker_id || null;
    const orgName = organizer?.name || null;

    // Always scroll so the CTA sits at the top, revealing content below it
    setTimeout(() => bottomSentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);

    if (isKarmana) {
      // Karmana: show prompt message
      setShowRegMessage(true);

      if (user) {
        // Karmana + logged in: mark interested
        track('activity_interested', { activity_id: activity.id, activity_name: activity.name, registration_type: 'karmana', action: 'interested', cta_state: 'im_interested', organisation_id: orgId, organisation_name: orgName, source_page: 'activity_detail' });
        setInterestLoading(true);
        const wasInterested = interested;
        return toggleActivityInterest(activity.id).then(async r => {
          if (r.data) {
            queryClient.setQueryData(['engagement', activity.id], r.data);
            if (!wasInterested && r.data.interested) setInterestCount(c => c + 1);
            await queryClient.refetchQueries({ queryKey: ['my-activities'] });
          }
        }).catch(() => {}).finally(() => setInterestLoading(false));
      }
      // Karmana + logged out: prompt to sign up for registration
      track('signup_trigger', { trigger: 'karmana_detail_interested', registration_type: 'karmana', cta_state: 'im_interested', activity_id: activity.id, activity_name: activity.name, organisation_id: orgId, source_page: 'activity_detail' });
      return;
    }

    // Non-Karmana: auth check then mark interested
    if (!user) {
      track('signup_trigger', { trigger: 'activity_detail_interested', cta_state: 'im_interested', page: 'activity_detail', activity_id: activity?.id, activity_name: activity?.name, organisation_id: orgId, source_page: 'activity_detail' });
      localStorage.setItem('karmana_pending_interest', JSON.stringify({
        activityId: activity.id,
        returnPath: `/activities/${activity.slug || activity.id}`,
      }));
      openAuthModal('interested');
      return;
    }
    setInterestLoading(true);
    const wasInterested = interested;
    return toggleActivityInterest(activity.id).then(async r => {
      if (r.data) {
        queryClient.setQueryData(['engagement', activity.id], r.data);
        if (!wasInterested && r.data.interested) setInterestCount(c => c + 1);
        await queryClient.refetchQueries({ queryKey: ['my-activities'] });
      }
      // Non-Karmana + logged in: track toggle outcome
      track('activity_interested', { activity_id: activity.id, activity_name: activity.name, action: r.data?.interested ? 'interested' : 'uninterested', cta_state: 'im_interested', organisation_id: orgId, organisation_name: orgName, source_page: 'activity_detail' });
    }).catch((err) => {
      throw err;
    }).finally(() => setInterestLoading(false));
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const organizers = (() => {
    if (!activity) return [];
    const primary = organizer ? [organizer] : [];
    // Additional co-organizers: use cached full list if available
    const cached = queryClient.getQueryData(['changemakers']) || [];
    const additional = (activity.organizer_ids || [])
      .filter(id => id !== activity.changemaker_id)
      .map(id => cached.find(c => c.id === id))
      .filter(Boolean);
    return [...primary, ...additional];
  })();

  const isPast = activity ? (
    activity.is_ongoing
      ? (!!activity.date_end && new Date(`${activity.date_end}T23:59:59`) < new Date())
      : (!!(activity.date && new Date(`${activity.date}T23:59:59`) < new Date()))
  ) : false;

  // True when the activity has fully passed (date_end if available, else date)
  const isExpired = activity ? (
    activity.is_ongoing
      ? (!!activity.date_end && new Date(`${activity.date_end}T23:59:59`) < new Date())
      : (
        activity.date_end
          ? new Date(`${activity.date_end}T23:59:59`) < new Date()
          : (activity.date ? new Date(`${activity.date}T23:59:59`) < new Date() : false)
      )
  ) : false;

  // Ref for the explore section so the CTA can scroll to it
  const exploreRef = useRef(null);
  // Show explore section only for signed-out users on expired activities
  const showExploreSection = isExpired && !user;
  // True once ExploreAfterExpiredSection reports ≥1 recommendations loaded
  const [hasRecs, setHasRecs] = useState(false);

  // ── Signed-out tutorial on detail page ────────────────────────────────────
  // Shows for non-past, non-expired activities only.
  // No session storage: each activity page starts a fresh 4s timer independently.
  // Dismissal on one activity does NOT suppress the tutorial on another activity.
  const [showDetailTutorial, setShowDetailTutorial] = useState(false);

  const dismissDetailTutorial = useCallback(() => {
    setShowDetailTutorial(false);
  }, []);

  useEffect(() => {
    if (user || !activity || isExpired || isPast) return;
    setShowDetailTutorial(false); // reset if activity changes
    const timer = setTimeout(() => {
      setShowDetailTutorial(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, [user, activity?.id, isExpired, isPast]); // eslint-disable-line react-hooks/exhaustive-deps

  // mapEmbedUrl is no longer used — map is handled by ActivityLocationMap component

  // ── Loading / Error states ────────────────────────────────────────────────

  if (isLoading) return (
    <Layout>
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
        <div className="md:max-w-[624px] md:mx-auto animate-pulse">
          {/* Back button */}
          <div className="px-4 pt-4 pb-2">
            <div className="h-4 w-32 bg-gray-200 rounded-full" />
          </div>
          {/* Image */}
          <div className="bg-gray-200 w-full aspect-video" />
          <div className="px-4 pt-4 space-y-3">
            {/* Title */}
            <div className="h-7 bg-gray-200 rounded-full w-3/4" />
            <div className="h-7 bg-gray-200 rounded-full w-1/2" />
            {/* Tags */}
            <div className="flex gap-2 pt-1">
              <div className="h-5 w-16 bg-gray-200 rounded-full" />
              <div className="h-5 w-20 bg-gray-200 rounded-full" />
            </div>
            {/* Meta: date / location */}
            <div className="pt-2 space-y-2">
              <div className="h-4 bg-gray-200 rounded-full w-40" />
              <div className="h-4 bg-gray-200 rounded-full w-52" />
            </div>
            {/* Description lines */}
            <div className="pt-3 space-y-2">
              <div className="h-4 bg-gray-200 rounded-full w-full" />
              <div className="h-4 bg-gray-200 rounded-full w-full" />
              <div className="h-4 bg-gray-200 rounded-full w-5/6" />
              <div className="h-4 bg-gray-200 rounded-full w-4/6" />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );

  // Draft access-gated error states
  if (isError) {
    const status = error?.response?.status;

    if (status === 401) return (
      <Layout>
        <div className="max-w-xl mx-auto px-4 py-20 text-center space-y-4">
          <p className="text-lg font-semibold text-gray-700">This page isn't available yet</p>
          <p className="text-sm text-gray-400">You need to sign in to view this page.</p>
          <button onClick={() => openAuthModal('signin')} className="mt-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors">
            Sign In
          </button>
          <div className="pt-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors">
              <Home size={15} /> Go Home
            </Link>
          </div>
        </div>
      </Layout>
    );

    if (status === 403) return (
      <Layout>
        <div className="max-w-xl mx-auto px-4 py-20 text-center space-y-4">
          <p className="text-lg font-semibold text-gray-700">This page isn't available yet</p>
          <p className="text-sm text-gray-400">This activity hasn't been published yet. Check back later.</p>
          <div className="pt-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors">
              <Home size={15} /> Go Home
            </Link>
          </div>
        </div>
      </Layout>
    );

    return (
      <Layout>
        <SEOHead statusCode={404} title="Activity Not Found" noIndex />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <p className="text-gray-500 mb-4">Activity not found.</p>
          <Link to="/activities" className="text-green-600 hover:underline">Go to All Activities</Link>
        </div>
      </Layout>
    );
  }

  if (!activity) return null;

  // ── Draft: render inline edit page ───────────────────────────────────────
  if (activity.status !== 'published') {
    const isAdmin = user?.is_admin;
    return (
      <Layout>
        <div className="max-w-[624px] mx-auto">
          <ActivityEditPage
            activity={activity}
            organizer={organizer || null}
            isPublished={false}
            onPublished={() => setOwnerEditMode(true)}
            {...(isAdmin ? {
              saveFn: updateActivity,
              publishFn: publishActivity,
              uploadFn: adminUploadMedia,
              isAdmin: true,
            } : {})}
          />
        </div>
      </Layout>
    );
  }

  // ── Published + owner/editor-collaborator in edit/view mode ─────────────────
  const isOwner = cmOrg?.id === activity.changemaker_id;
  const isEditorCollaborator = (activity.collaborators || []).some(
    c => c.org_id === cmOrg?.id && c.permission === 'editor'
  );
  if ((isOwner || isEditorCollaborator) && ownerEditMode) {
    const currentMode = searchParams.get('mode');
    return (
      <Layout>
        <div className="max-w-[624px] mx-auto">
          <ActivityEditPage
            activity={isEditorCollaborator ? { ...activity, is_collaboration: true } : activity}
            organizer={organizer || null}
            isPublished={true}
            onExitEdit={() => setOwnerEditMode(false)}
            initialEditMode={currentMode === 'edit'}
            onEditModeChange={(isEdit) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('mode', isEdit ? 'edit' : 'view');
                return next;
              }, { replace: true });
            }}
          />
        </div>
      </Layout>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      {activity && (
        <SEOHead
          title={activity.name}
          description={buildActivitySocialDesc(activity) ||
            `Join ${activity.name} — a sustainability activity in Bengaluru.`}
          canonical={`https://karmana.in/activities/${activity.slug || activity.id}`}
          ogImage={getActivityImage(activity)}
          ogType="event"
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: activity.name,
            description: truncateDesc(activity.description, 500) || '',
            url: `https://karmana.in/activities/${activity.slug || activity.id}`,
            ...(getActivityImage(activity) ? { image: getActivityImage(activity) } : {}),
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            ...(activity.date ? { startDate: activity.date + (activity.start_time ? `T${activity.start_time}` : '') } : {}),
            ...(activity.date_end ? { endDate: activity.date_end + (activity.end_time ? `T${activity.end_time}` : '') } : {}),
            location: activity.location?.address
              ? {
                  '@type': 'Place',
                  name: activity.location.address,
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: activity.location.address,
                    addressLocality: activity.location.locality || 'Bengaluru',
                    addressRegion: 'Karnataka',
                    addressCountry: 'IN',
                  },
                }
              : {
                  '@type': 'Place',
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: 'Bengaluru',
                    addressRegion: 'Karnataka',
                    addressCountry: 'IN',
                  },
                },
            organizer: organizer
              ? {
                  '@type': 'Organization',
                  name: organizer.name,
                  url: `https://karmana.in/organizations/${organizer.slug || organizer.id}`,
                }
              : activity.other_organizer_name
              ? {
                  '@type': 'Organization',
                  name: activity.other_organizer_name,
                  ...(activity.other_organizer_website ? { url: activity.other_organizer_website } : {}),
                }
              : undefined,
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'INR',
              availability: 'https://schema.org/InStock',
              url: activity.registration_link || `https://karmana.in/activities/${activity.slug || activity.id}`,
            },
          }}
        />
      )}
      {/* ── Outer page container — matches Activities page width ── */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
        <div className="md:max-w-[624px] md:mx-auto">



      {/* Content — full-width, no card wrapper, matches ActivityCard on activities page */}
      <div className="bg-white mt-3" style={{ overflow: 'clip' }}>

          <div className="px-4 pt-4">
            {/* ── Sentinel + Sticky Heading ── */}
            <div ref={headingSentinelRef} className="h-0 w-full" aria-hidden="true" />
            <div className="relative">
              <h1
                className={`text-2xl font-bold text-gray-900 leading-tight ${
                  headingSticky
                    ? 'sticky top-14 bg-white z-10 -mx-4 px-4 pt-2 pb-2 mb-1 shadow-[0_2px_6px_rgba(0,0,0,0.06)]'
                    : 'mb-2.5'
                } ${(activity.registration_type === 'karmana' || getFormEmbedUrl(activity)) ? 'pr-10' : ''}`}
                data-testid="detail-heading"
              >
                {activity.name}
              </h1>
              {/* Share icon — top-right of heading, Karmana and embedded-form activities */}
              {(activity.registration_type === 'karmana' || getFormEmbedUrl(activity)) && (
                <div className="absolute top-0 right-0">
                  <ShareButton
                    activityId={activity.id}
                    activityName={activity.name}
                    activitySlug={activity.slug}
                    variant="icon-only"
                    cardContext={{
                      activity_id: activity.id,
                      activity_name: activity.name,
                      organisation_id: activity.changemaker_id || null,
                      organisation_name: organizer?.name || null,
                      source_page: 'activity_detail',
                    }}
                  />
                </div>
              )}
            </div>
            {/* Slots left badge — shown below heading for capacity-limited activities */}
            {activity.is_participants_fixed && activity.slots_left != null && (
              <div className="mb-2.5 -mt-1">
                <SlotsLeftBadge slotsLeft={activity.slots_left} />
              </div>
            )}
            {/* Completed tag — shown for expired activities, below heading */}
            {isExpired && (
              <div className="flex items-center gap-3 mb-2 -mt-1">
                <span
                  className="text-sm font-bold px-2.5 py-1 rounded-full"
                  style={{ background: '#fff7ed', color: '#c2410c' }}
                  data-testid="activity-completed-tag"
                >
                  Completed
                </span>
                {showExploreSection && hasRecs && (
                  <button
                    onClick={() => exploreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                    data-testid="explore-similar-link"
                  >
                    Explore Similar Activities <ArrowRight size={13} />
                  </button>
                )}
              </div>
            )}

            {/* ── Interest tags ── */}
            {activity.areas_of_interest?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {activity.areas_of_interest.map(id => {
                  const interest = interests.find(i => i.id === id);
                  if (!interest) return null;
                  return (
                    <span key={id} className="px-2 py-0.5 bg-white text-gray-600 text-[11px] rounded-full flex items-center gap-1 border border-gray-300" data-testid={`detail-interest-${id}`}>
                      <InterestIcon icon={interest.icon} size={11} className="text-gray-500 flex-shrink-0" />
                      {interest.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Carousel ── */}
          {carouselPhotos.length > 0 && (
            <div className="relative -mx-0 mb-0 group" data-testid="detail-carousel">
              <div
                ref={carouselRef}
                className="flex overflow-x-auto snap-x snap-mandatory"
                style={{
                  scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch',
                  ...(detailCarouselHeight ? { height: detailCarouselHeight + 'px', transition: 'height 0.3s ease' } : {}),
                }}
                onScroll={handleCarouselScroll}
              >
                {carouselPhotos.map((url, i) => (
                  <div key={i} className="flex-shrink-0 w-full snap-center bg-gray-50 relative" style={detailCarouselHeight ? { height: detailCarouselHeight + 'px' } : {}}>
                    {isVideoUrl(url) ? (
                      <div className={`relative w-full ${detailCarouselHeight ? 'h-full' : 'aspect-[4/5] md:aspect-[16/9]'}`}>
                        <video
                          src={url}
                          className="w-full h-full object-contain cursor-pointer"
                          onClick={() => { track('activity_media_click', { activity_id: activity?.id, media_index: i, media_type: 'video', media_count: carouselPhotos.length }); lightboxOpenTimeRef.current = Date.now(); setLightboxIdx(i); setLightboxOpen(true); track('activity_lightbox_open', { activity_id: activity?.id, media_index: i }); }}
                          preload="metadata"
                          playsInline
                          muted
                          loop
                          onLoadedMetadata={(e) => {
                            const { videoWidth: w, videoHeight: h } = e.target;
                            if (w > 0) setDetailImgNaturals(prev => ({ ...prev, [i]: { w, h } }));
                          }}
                        />
                        {carouselIdx !== i && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center">
                              <div className="w-0 h-0 border-t-[10px] border-b-[10px] border-l-[18px] border-transparent border-l-white ml-1" />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <img
                        src={url}
                        alt={`${activity.name} photo ${i + 1}`}
                        className={`w-full cursor-pointer object-contain ${detailCarouselHeight ? 'h-full' : 'aspect-[4/5] md:aspect-[16/9]'}`}
                        onClick={() => { track('activity_media_click', { activity_id: activity?.id, media_index: i, media_type: 'image', media_count: carouselPhotos.length }); lightboxOpenTimeRef.current = Date.now(); setLightboxIdx(i); setLightboxOpen(true); track('activity_lightbox_open', { activity_id: activity?.id, media_index: i }); }}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        onLoad={(e) => {
                          const { naturalWidth: w, naturalHeight: h } = e.target;
                          if (w > 0) setDetailImgNaturals(prev => ({ ...prev, [i]: { w, h } }));
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {/* Dot indicators */}
              {carouselPhotos.length > 1 && (
                <div className="flex justify-center gap-1.5 py-2 bg-white" data-testid="detail-carousel-dots">
                  {carouselPhotos.map((_, i) => (
                    <div key={i} className={`rounded-full transition-all ${i === carouselIdx ? 'w-4 h-1.5 bg-gray-700' : 'w-1.5 h-1.5 bg-gray-300'}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="px-4 pt-3 pb-4 space-y-5">
            {/* ── Description (no label, fully open) ── */}
            {activity.description && (
              <div data-testid="detail-description">
                <SafeHtml html={activity.description} className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none" />
              </div>
            )}

            {/* ── Things to bring ── */}
            {activity.things_to_bring && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Things to Bring</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{activity.things_to_bring}</p>
              </div>
            )}

            {/* ── Organised By ── */}
            {organizers.length > 0 && (
              <div data-testid="detail-organised-by">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Organised by</p>
                <div className="flex flex-col gap-3">
                  {organizers.map(cm => {
                    const shortDesc = cm.short_description
                      ? (cm.short_description.length > 150 ? cm.short_description.slice(0, 150) + '…' : cm.short_description)
                      : (cm.description ? cm.description.replace(/<[^>]*>/g, '').slice(0, 150) + (cm.description.replace(/<[^>]*>/g, '').length > 150 ? '…' : '') : '');
                    return (
                      <div key={cm.id}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 cursor-pointer min-w-0" data-testid={`detail-organizer-${cm.id}`} onClick={() => track('organisation_click', { organisation_id: cm.id, organisation_name: cm.name, source_page: 'activity_detail', activity_id: activity?.id })}>
                            {cm.logo_url
                              ? <img src={cm.logo_url} alt={cm.name} className="w-5 h-5 rounded-full object-contain bg-white flex-shrink-0" />
                              : <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center text-[9px] font-bold text-green-700 flex-shrink-0">{cm.name[0]}</div>
                            }
                            <span className="text-sm font-medium text-gray-700 truncate">{cm.name}</span>
                            {cm.is_claimed
                              ? <VerifiedBadge />
                              : <UnverifiedBadge orgId={cm.id} orgName={cm.name} />}
                          </div>
                          <HeartButton
                            changemakerId={cm.id}
                            orgName={cm.name}
                            trigger="activity_detail_follow"
                            size={20}
                            className="flex-shrink-0 px-1"
                          />
                        </div>
                        {shortDesc && (
                          <p className="text-xs text-gray-500 mt-1.5 px-1 leading-relaxed">{shortDesc}</p>
                        )}
                      </div>
                    );
                  })}

                  {/* Collaborating orgs */}
                  {(activity?.collaborators || []).map(collab => {
                    const relLabel = collab.relationship_type === 'other'
                      ? collab.relationship_custom_text
                      : { organiser: 'Organised By', co_organiser: 'Co-organised by', partner: 'In partnership with', sponsor: 'Sponsored by' }[collab.relationship_type] || 'Co-organised by';
                    return (
                      <div key={collab.id} data-testid={`detail-collaborator-${collab.id}`}>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{relLabel}</p>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 min-w-0">
                            {collab.org_logo_url
                              ? <img src={collab.org_logo_url} alt={collab.org_name} className="w-5 h-5 rounded-full object-contain bg-white flex-shrink-0" />
                              : <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-600 flex-shrink-0">{(collab.org_name || '?')[0]}</div>}
                            <span className="text-sm font-medium text-gray-700 truncate">{collab.org_name}</span>
                            {collab.org_id && (collab.org_is_claimed
                              ? <VerifiedBadge />
                              : <UnverifiedBadge orgId={collab.org_id} orgName={collab.org_name} />)}
                          </div>
                          {collab.org_id && (
                            <HeartButton
                              changemakerId={collab.org_id}
                              orgName={collab.org_name}
                              trigger="activity_detail_follow_collaborator"
                              size={20}
                              className="flex-shrink-0 px-1"
                            />
                          )}
                        </div>
                        {collab.org_short_description && (
                          <p className="text-xs text-gray-500 mt-1.5 px-1 leading-relaxed">
                            {collab.org_short_description.length > 150 ? collab.org_short_description.slice(0, 150) + '…' : collab.org_short_description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Skills required ── */}
            {activity.skills_required && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Skills Required</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{activity.skills_required}</p>
              </div>
            )}

            {/* ── Happening On ── */}
            <HappeningOnBlock activity={activity} />

            {/* ── Happening At ── */}
            <HappeningAtBlock activity={activity} alreadyRegistered={alreadyRegistered} />

            <div className="pb-4" aria-hidden="true" />

          </div>
      </div>{/* end content */}

      {/* Bottom sentinel — when visible, sticky buttons unpin */}
      <div ref={bottomSentinelRef} className="h-0" aria-hidden="true" />

      {/* Spacer: reserves space for the fixed footer when it's sticky; collapses when footer unpins */}
      <div className={`transition-all duration-150 ${bottomVisible ? 'h-0' : 'h-16'}`} aria-hidden="true" />

      {/* ── Join/Share footer: fixed while scrolling, inline at page bottom
           Hidden for Karmana registration activities and embedded-form activities ── */}
      {activity.registration_type !== 'karmana' && !getFormEmbedUrl(activity) && (
      <div
        className={`${bottomVisible ? '' : 'fixed bottom-0 left-0 right-0 z-30'} bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)]`}
        data-testid="detail-sticky-footer"
      >
        <div className="md:max-w-[624px] md:mx-auto">
        <ActivityJoinSection
          activityId={activity.id}
          activityName={activity.name}
          activitySlug={activity.slug}
          shareCardContext={{
            activity_id: activity.id,
            activity_name: activity.name,
            organisation_id: activity.changemaker_id || null,
            organisation_name: organizer?.name || null,
            source_page: 'activity_detail',
          }}
          isPast={isPast}
          interested={interested}
          interestLoading={interestLoading}
          onJoin={handleInterested}
          registrationLink={activity.registration_link}
          registrationClosed={activity.registration_closed || (activity.is_participants_fixed && activity.slots_left === 0)}
          registrationType={activity.registration_type}
          registrationFields={activity.registration_form_fields}
          registrationRequirement={activity.registration_requirement}
          alreadyRegistered={alreadyRegistered}
          autoShowSuccess={autoShowSuccess}
          onAutoShowSuccessConsumed={() => setAutoShowSuccess(false)}
          onRegisterButtonClick={() => trackRegistrationClick(activity.id, 'detail_button').catch(() => {})}
          onRegisterClick={() => trackRegistrationClick(activity.id, 'detail_link').catch(() => {})}
          onRegistered={() => {
            queryClient.invalidateQueries({ queryKey: ['engagement', activity.id] });
            queryClient.invalidateQueries({ queryKey: ['my-activities'] });
          }}
          onSignIn={(newUser) => { /* user context updated by ActivityJoinSection */ }}
          inlineOnly={true}
          onScrollToInlineReg={() => {
            trackRegistrationClick(activity.id, 'detail_button').catch(() => {});
            setTimeout(() => regBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
          }}
          pendingOTPData={pendingOTPForSignUp}
          onReopenOTP={() => setOtpData(pendingOTPForSignUp)}
          exploreOverride={(showExploreSection && hasRecs) ? {
            text: 'This Activity is Over',
            persistentMsg: 'Missed This? Explore Similar Activities',
            onClick: () => {
              track('explore_expired_cta_click', { activity_id: activity?.id, activity_slug: activity?.slug });
              exploreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
          } : null}
          showTutorial={showDetailTutorial}
          onTutorialDismiss={dismissDetailTutorial}
          embedUrl={getFormEmbedUrl(activity)}
        />
        </div>
      </div>
      )}

      {/* ── Inline Registration Block ──
           Karmana: always visible
           Embedded form: always visible (no interest gate — form IS the registration)
           External link: only when interested ── */}
      {(activity.registration_type === 'karmana' ||
        getFormEmbedUrl(activity) !== null ||
        (interested && activity.registration_link && !activity.registration_closed)) && (
        <div className="md:max-w-[624px] md:mx-auto" data-testid="detail-reg-section">
          <ActivityRegistrationBlock
            activityId={activity.id}
            registrationType={activity.registration_type}
            registrationLink={activity.registration_link}
            registrationClosed={activity.registration_closed || (activity.is_participants_fixed && activity.slots_left === 0)}
            registrationFull={!!(activity.is_participants_fixed && activity.slots_left === 0)}
            registrationFields={activity.registration_form_fields}
            registrationRequirement={activity.registration_requirement}
            registrationEndDate={activity.registration_end_date}
            alreadyRegistered={alreadyRegistered}
            user={user}
            activityDate={activity.date}
            activityDateEnd={activity.date_end}
            isOngoing={activity.is_ongoing}
            embedUrl={getFormEmbedUrl(activity)}
            onSuccess={() => {
              setShowRegMessage(false);
              queryClient.invalidateQueries({ queryKey: ['engagement', activity.id] });
              queryClient.invalidateQueries({ queryKey: ['my-activities'] });
              // If journey is enabled, show 4-sec non-dismissible countdown then scroll to Journey
              if (activity.activity_journey === true) {
                let count = 4;
                setRegSuccessJourneyCountdown(count);
                const iv = setInterval(() => {
                  count--;
                  if (count <= 0) {
                    clearInterval(iv);
                    setRegSuccessJourneyCountdown(null);
                    setTimeout(() => journeyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                  } else {
                    setRegSuccessJourneyCountdown(count);
                  }
                }, 1000);
              }
            }}
            onOTPRequired={(data) => {
              const otpInfo = { ...data, activityId: activity.id };
              setOtpData(otpInfo);
              setPendingOTPForSignUp(otpInfo); // Track for "You're all set. Sign up" CTA (GAP 3)
            }}
            blockRef={regBlockRef}
            showMessage={showRegMessage}
            sourceProps={{
              activity_id: activity.id,
              activity_name: activity.name,
              organisation_id: activity.changemaker_id || null,
              organisation_name: organizers[0]?.name || null,
              source_page: 'activity_detail',
              activity_position: null,
            }}
          />
        </div>
      )}

      {/* ── Public Comments — above Journey; visible to all visitors ── */}
      {activity.activity_journey === true && (
        <div className="md:max-w-[624px] md:mx-auto bg-white border-t border-gray-100" data-testid="activity-public-comments">
          <ActivityCommentsSection activity={activity} showForm={false} />
        </div>
      )}

      {/* ── Activity Journey (visible to all visitors when activity_journey is enabled) ── */}
      {activity.activity_journey === true && (
        <div ref={journeyRef} className="md:max-w-[624px] md:mx-auto border-t border-gray-100 pt-4" data-testid="activity-journey-section">
          <ActivityJourney
            activity={activity}
            organizer={organizer || null}
            alreadyRegistered={alreadyRegistered}
            isInterested={interested}
            registrationType={activity.registration_type || null}
            isExpired={isExpired}
            onScrollToForm={() => {
              setTimeout(() => regBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
            onScrollToInterested={() => {
              setTimeout(() => bottomSentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
            installCTA={installCTANode}
          />
        </div>
      )}

      {/* Activity Updates Section */}
      {activityUpdates.length > 0 && (        <div id="activity-updates" className="px-4 pb-4 mt-6 md:max-w-[624px] md:mx-auto" data-testid="activity-updates-section">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-4">Activity Updates</p>
          <div className="space-y-6">
            {activityUpdates.map((upd) => (
              <div key={upd.id} className="pb-6 border-b border-gray-100 last:border-b-0">
                <p className="text-[11px] font-semibold text-green-600 uppercase tracking-wide mb-1" data-testid={`update-timestamp-${upd.id}`}>
                  Update Shared on {formatUpdateTimestamp(upd.published_at)}
                </p>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{upd.heading}</h3>
                {upd.media_urls?.length > 0 && (
                  <UpdateMediaCarousel urls={upd.media_urls} mediaNames={upd.media_names} />
                )}
                {upd.content && (
                  <SafeHtml html={upd.content} className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none mt-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Explore After Expired section (signed-out users only) ── */}
      {showExploreSection && (
        <div className="md:max-w-[624px] md:mx-auto px-4 mt-8">
          <ExploreAfterExpiredSection
            activitySlug={activity?.slug || slugOrId}
            sectionRef={exploreRef}
            onRecsLoaded={(count) => setHasRecs(count > 0)}
          />
        </div>
      )}

      {/* Explore All Activities CTA */}
      <div className="px-4 pb-8 mt-6 md:max-w-[624px] md:mx-auto" data-testid="detail-show-all-cta">
        <Link
          to="/activities"
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors"
          data-testid="see-all-activities-btn"
          onClick={() => track('nav_explore_activities', { source_page: 'activity_detail', activity_id: activity?.id })}
        >
          Explore All Activities
          <ArrowRight size={14} />
        </Link>
      </div>


        </div>{/* end md:max-w-[624px] */}
      </div>{/* end max-w-7xl */}

      {/* ── Lightbox ── */}
      {lightboxOpen && carouselPhotos.length > 0 && (
        <Lightbox
          photos={carouselPhotos}
          idx={lightboxIdx}
          onClose={() => { track('activity_lightbox_close', { activity_id: activity?.id, time_open_ms: lightboxOpenTimeRef.current ? Date.now() - lightboxOpenTimeRef.current : null }); setLightboxOpen(false); }}
          onSetIdx={(newIdx) => { const isPrev = newIdx === (lightboxIdx - 1 + carouselPhotos.length) % carouselPhotos.length; track('activity_lightbox_nav', { activity_id: activity?.id, media_index_after: newIdx, direction: isPrev ? 'prev' : 'next' }); setLightboxIdx(newIdx); }}
        />
      )}

      {/* ── OTP Activation Modal (page-level, persists after form unmounts) ── */}
      {otpData && (
        <RegistrationOTPModal
          activityId={otpData.activityId}
          method={otpData.method}
          contact={otpData.email}
          pendingRegId={otpData.pendingRegId}
          email={otpData.email}
          confirmResult={otpData.confirmResult}
          sourceProps={{
            activity_id: activity.id,
            organisation_id: activity.changemaker_id || null,
            organisation_name: organizers[0]?.name || null,
            source_page: 'activity_detail',
          }}
          onSuccess={(verifiedUser) => {
            if (verifiedUser) setUser(verifiedUser);
            queryClient.invalidateQueries({ queryKey: ['engagement', activity.id] });
            queryClient.invalidateQueries({ queryKey: ['my-activities'] });
            setOtpData(null);
            setPendingOTPForSignUp(null);
          }}
          onClose={() => {
            track('karmana_reg_abandoned', {
              activity_id: activity.id,
              step: 'otp',
              otp_method: otpData?.method,
            });
            setOtpData(null);
          }}
        />
      )}

      {/* ── Karmana registration success → Journey countdown overlay (non-dismissible) ── */}
      {regSuccessJourneyCountdown !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="reg-success-journey-overlay">
          <div className="bg-white rounded-2xl p-8 text-center shadow-xl max-w-sm mx-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={22} className="text-green-600" />
            </div>
            <p className="font-bold text-gray-900 mb-1">Registration Successful!</p>
            <p className="text-sm text-gray-500">Taking you to your Activity Journey in {regSuccessJourneyCountdown}s…</p>
          </div>
        </div>
      )}

    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HappeningOnBlock({ activity }) {
  const isOngoingNoDate = activity.is_ongoing && !activity.date;
  let label = '';
  if (isOngoingNoDate) {
    label = activity.date_end ? `Ongoing · Last Date: ${formatDateRange(activity.date_end, null)}` : 'Ongoing';
  } else {
    label = formatDateRange(activity.date, activity.date_end);
  }
  if (!label) return null;
  return (
    <div data-testid="detail-happening-on">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Happening On</p>
      <div className="w-full bg-gray-50 rounded-xl overflow-hidden px-3 py-2.5">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Clock size={14} className="text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">{label}</p>
            {activity.time && <p className="text-xs text-gray-500 mt-0.5">{activity.time}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function HappeningAtBlock({ activity, alreadyRegistered }) {
  const loc = activity?.location;
  const howToReach = activity?.how_to_reach || '';
  const isOnline = loc?.is_online || false;
  const registrationType = activity?.registration_type;

  const address = loc?.address || '';
  const hasLocation = !!(loc?.latitude && loc?.longitude) || !!loc?.map_link || !!address;

  if (!isOnline && !hasLocation && !howToReach) return null;
  if (isOnline && !howToReach && !loc?.map_link) {
    // Online with no meeting link — still render the "Online" block
  }

  // Meeting link: map_link is repurposed for online activities
  const meetingLink = isOnline ? (loc?.map_link || null) : null;
  // Show meeting link: always for non-karmana; only after registration for karmana
  const showMeetingLink = meetingLink && (registrationType !== 'karmana' || alreadyRegistered);
  const meetingLinkLocked = meetingLink && registrationType === 'karmana' && !alreadyRegistered;

  // Physical "Open in Maps" URL
  const mapsOpenUrl = !isOnline ? (loc?.map_link || null) : null;

  return (
    <div data-testid="detail-happening-at">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Happening At</p>
      <div className="w-full bg-gray-50 rounded-xl overflow-hidden">

        {/* Online: show "Online" label */}
        {isOnline ? (
          <div className="flex items-start gap-2.5 px-3 py-2.5">
            <Globe size={15} className="text-teal-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Location</p>
              <p className="text-sm text-teal-700 font-medium leading-snug">Online</p>
            </div>
          </div>
        ) : address ? (
          /* Physical: show address row */
          <div className="flex items-start gap-2.5 px-3 py-2.5">
            <MapPin size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Address</p>
              <p className="text-sm text-gray-700 font-medium leading-snug">{address}</p>
            </div>
          </div>
        ) : null}

        {/* Meeting link (online only) — visible to non-karmana always, karmana after registration */}
        {showMeetingLink && (
          <a
            href={meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2.5 px-3 py-2.5 border-t border-gray-100 hover:bg-gray-100 transition-colors"
            data-testid="detail-meeting-link"
          >
            <ExternalLink size={15} className="text-teal-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Meeting Link</p>
              <p className="text-sm text-teal-600 font-medium leading-snug truncate">{meetingLink}</p>
            </div>
          </a>
        )}

        {/* Locked meeting link hint (karmana not yet registered) */}
        {meetingLinkLocked && (
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100" data-testid="detail-meeting-link-locked">
            <Lock size={13} className="text-gray-400 flex-shrink-0" />
            <p className="text-xs text-gray-500">Meeting link available after registration</p>
          </div>
        )}

        {/* Physical: interactive Google Maps widget */}
        {!isOnline && hasLocation && (
          <ActivityLocationMap
            latitude={loc.latitude}
            longitude={loc.longitude}
            mapLink={loc.map_link}
            address={address}
            className={`${address ? 'border-t border-gray-100' : ''}`}
            style={{ height: 160 }}
          />
        )}

        {/* Physical: Open in Maps link */}
        {!isOnline && mapsOpenUrl && (
          <a
            href={mapsOpenUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 py-1.5 text-xs text-green-700 font-medium hover:bg-gray-100 transition-colors border-t border-gray-100"
            data-testid="detail-open-in-maps"
          >
            Open in Maps <ExternalLink size={10} />
          </a>
        )}

        {/* How to reach — public, visible to all visitors */}
        {howToReach && (
          <div className="flex items-start gap-2.5 px-3 py-2.5 border-t border-gray-100" data-testid="detail-how-to-reach">
            <MapPin size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">How to reach</p>
              <p className="text-sm text-gray-700 leading-snug">{howToReach}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
